import { AlertCircle } from "lucide-react";
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/axios";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const BugReporter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [bugReport, setBugReport] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const handleSubmit = async () => {
    if (!bugReport.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.post("bug_report", {
        bug_report: bugReport,
      });

      if (response.status === 200) {
        setSuccess(true);
        setBugReport("");
        setTimeout(() => {
          setIsOpen(false);
          setTimeout(() => setSuccess(false), 300);
        }, 2000);
      }
    } catch (err) {
      setError("Error al enviar el reporte. Por favor, inténtalo de nuevo.");
      console.error("Error submitting bug report:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't show the button if user is not authenticated
  if (!isAuthenticated) return null;

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
        >
          <AlertCircle className="h-5 w-5 mr-2" />
          Reportar error/sugerencia
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reportar error o sugerencia</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {success ? (
              <div className="bg-green-100 dark:bg-green-900 p-4 rounded-md text-green-800 dark:text-green-200">
                ¡Gracias por tu reporte! Lo revisaremos lo antes posible.
              </div>
            ) : (
              <>
                <textarea
                  className="w-full h-32 p-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Describe el error o sugerencia que quieres reportar..."
                  value={bugReport}
                  onChange={(e) => setBugReport(e.target.value)}
                  disabled={isSubmitting}
                />

                {error && (
                  <div className="mt-2 text-destructive text-sm">{error}</div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            {!success && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !bugReport.trim()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {isSubmitting ? "Enviando..." : "Enviar reporte"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugReporter;
